import "./circle-countdown.scss";

import { TXLogger } from "@tx/framework";
import { observer } from "mobx-react-lite";
import { useRef } from "react";
import {
	ColorFormat,
	CountdownCircleTimer,
	MultipleColors,
} from "react-countdown-circle-timer";

import { useRootContext } from "@client/context/RootContext";
import useCircleCountdown from "@client/hooks/useCircleCountdown";
import { Order } from "@client/stores";

import { CircleCountdownModals } from "./circle-countdown-modals";

interface CircleCountdownProps {
	order: Order;
	closeOrderCardModal?: () => any;
}

const lastSeconds = 120;

export const CircleCountdown = observer(
	({ order, closeOrderCardModal }: CircleCountdownProps) => {
		const rootContext = useRootContext();
		const {
			countdownDuration,
			firstInterval,
			secondInterval,
			colors,
			isMounted,
			remainingTime,
			isNew,
		} = useCircleCountdown(order);

		const currentTime = useRef(remainingTime);

		const circleCountdownModal = useRef(
			CircleCountdownModals(order, rootContext)
		);
		const cancelModalOpen = useRef(false);

		/** when a race condition from changing (status + txOrderDates) + order data pull happens, changes.accepted.can_accept_until may return undefned */
		const hasBadTimeData =
			!countdownDuration || !firstInterval || !secondInterval || !remainingTime;

		const showCountdown = currentTime.current < lastSeconds;
		const timebombsEnabled = process.env.REACT_APP_TIMEBOMBS_ENABLED == "1";

		/* Timer computaion region */

		return hasBadTimeData ? null : (
			<div
				style={{
					visibility: showCountdown ? "visible" : "hidden",
				}}
			>
				<CountdownCircleTimer
					isPlaying
					duration={countdownDuration}
					initialRemainingTime={remainingTime}
					size={56}
					strokeWidth={3}
					isSmoothColorTransition={false}
					colors={
						[
							colors.green,
							colors.orange,
							colors.red,
							colors.red,
						] as MultipleColors["colors"]
					}
					colorsTime={[countdownDuration, firstInterval, secondInterval, 0]}
					trailColor={colors.gray as ColorFormat}
					trailStrokeWidth={1}
					onComplete={(totalElapsedTime) => {
						if (!timebombsEnabled) {
							return;
						}

						if (!cancelModalOpen.current) {
							TXLogger.trace("CircleCountdown()", "cancelling order", {
								extra: { totalElapsedTime, isNew, isMounted, order },
							});

							cancelModalOpen.current = true;
							closeOrderCardModal && closeOrderCardModal();
							circleCountdownModal.current.show_timeUpModal();
						}
						return { shouldRepeat: false };
					}}
				>
					{({ remainingTime }) => {
						const minutes = Math.floor(remainingTime / 60);
						const seconds = remainingTime % 60;

						currentTime.current = remainingTime;

						return (
							<div className="timer" style={{ textAlign: "center" }}>
								<div
									className="value"
									style={{
										fontSize: "20px",
										fontWeight: 500,
										color:
											remainingTime < lastSeconds ? colors.red : colors.black,
									}}
								>
									{minutes}
								</div>
								<div
									className="text"
									style={{
										fontSize: "10px",
										color:
											remainingTime < lastSeconds ? colors.red : colors.black,
										marginTop: "-2px",
										paddingBottom: "3px",
									}}
								>
									{seconds}
								</div>
							</div>
						);
					}}
				</CountdownCircleTimer>
			</div>
		);
	}
);
